import { QueryOptions } from 'interfaces/queryOptions'
import { list as baseList, listDeviceTypes } from 'services/devices'
import {
  list as baseInventoryList,
  createBatch as createBillingInventoryBatch
} from 'services/billing/inventories'

export interface DeviceListResponse {
  id: number
  enterprise?: string
  imei?: string
  name?: string
  nature?: string
  brand_alias?: string
  alias?: string
  last_connection_time?: Date
  last_gps_time?: Date
}

export interface AvailableDeviceResponse {
  id: number
  sku?: string
  imei?: string
  name?: string
  nature?: string
  brand_alias?: string
  alias?: string
}

export interface ListResponse {
  count: number
  devices: DeviceListResponse[]
}

export interface AvailableListResponse {
  count: number
  devices: AvailableDeviceResponse[]
}

export const list = async (
  queryOptions: QueryOptions
): Promise<ListResponse> => {
  const { data, count } = await baseList(queryOptions)

  const devices = data.map((device) => {
    const deviceAttributes = device.attributes.attributes
    const deviceTypeAttributes =
      device.relationships?.type?.attributes.attributes ?? {}
    const deviceLastGpsAttributes =
      device.relationships?.last_gps?.attributes.attributes ?? {}
    const enterpriseAttributes =
      device.relationships?.enterprise?.attributes.attributes ?? {}

    return {
      id: device.id,
      enterprise: enterpriseAttributes.name,
      imei: deviceAttributes.imei,
      name: deviceAttributes.name,
      nature: deviceAttributes.nature,
      brand_alias: deviceTypeAttributes.brand_alias,
      alias: deviceTypeAttributes.alias,
      last_connection_time: deviceLastGpsAttributes.last_connection_time,
      last_gps_time: deviceLastGpsAttributes.last_gps_time
    }
  })

  return { count, devices }
}

// "Disponíveis" — itens de billing_inventories (estoque) ainda não alocados a nenhum pedido
export const listAvailable = async (
  options: QueryOptions = {}
): Promise<AvailableListResponse> => {
  const result = await baseInventoryList({
    ...options,
    filters: [
      { key: 'type', op: 'eq', value: 'device' },
      { key: 'status', op: 'eq', value: 'available' },
      ...(options.filters ?? [])
    ]
  })

  const items = result.data.map((item: any) => {
    const attributes = item.attributes
    const meta = attributes.meta ?? {}

    return {
      id: item.id,
      sku: attributes.sku,
      imei: attributes.identifier,
      name: meta.name,
      nature: attributes.nature,
      deviceTypeId: meta.device_type_id
    }
  })

  const hasDeviceType = items.some((item) => item.deviceTypeId != null)

  // device_type_id ja chega decodificado (id numerico real, nao o hash publico) -
  // por isso nao da pra filtrar de volta por id via querystring (o gateway tentaria
  // decodificar de novo e falharia). Busca o catalogo inteiro e casa localmente.
  const deviceTypesById = hasDeviceType ? await getDeviceTypesById() : {}

  const devices = items.map(({ deviceTypeId, ...item }) => ({
    ...item,
    brand_alias: deviceTypesById[deviceTypeId as number]?.brand_alias,
    alias: deviceTypesById[deviceTypeId as number]?.alias
  }))

  return { count: result.count, devices }
}

const DEVICE_TYPES_PAGE_SIZE = 100

export const getDeviceTypesById = async (): Promise<
  Record<number, { brand_alias?: string; alias?: string; name?: string }>
> => {
  const deviceTypesById: Record<
    number,
    { brand_alias?: string; alias?: string; name?: string }
  > = {}

  let page = 1
  let hasMore = true

  while (hasMore) {
    const deviceTypes = await listDeviceTypes({
      attributes: ['id', 'brand_alias', 'alias', 'name'],
      limit: DEVICE_TYPES_PAGE_SIZE,
      page
    })

    deviceTypes.forEach((dt: Record<string, any>) => {
      const dtAttributes = dt.attributes.attributes
      deviceTypesById[dt.id] = {
        brand_alias: dtAttributes.brand_alias,
        alias: dtAttributes.alias,
        name: dtAttributes.name
      }
    })

    hasMore = deviceTypes.length === DEVICE_TYPES_PAGE_SIZE
    page += 1
  }

  return deviceTypesById
}

export const create = async (
  attributes: Record<string, any>[]
): Promise<void> => {
  if (!attributes.length) return

  const payload = attributes.map(
    ({
      imei,
      name,
      device_type_id,
      nature,
      purchase_tax_note,
      purchase_tax_note_date,
      tax_note_series,
      batch,
      unit_cost,
      supplier_id,
      buyer_id,
      country_id,
      currency
    }) => ({
      attributes: {
        type: 'device',
        identifier: imei,
        nature,
        tax_note_number: purchase_tax_note,
        tax_note_date: purchase_tax_note_date,
        tax_note_series,
        batch,
        unit_cost,
        supplier_id,
        buyer_id,
        country_id,
        currency,
        meta: { name, device_type_id }
      }
    })
  )

  await createBillingInventoryBatch(payload)
}

export const getDeviceTypes = async (
  search: string
): Promise<
  [
    {
      id: number
      brand_alias: string
      alias: string
      supported: false
      type: string
    }
  ]
> => {
  const deviceTypes = await listDeviceTypes({
    attributes: ['id', 'brand_alias', 'alias', 'supported', 'type'],
    search
  })

  return deviceTypes.map((dt: Record<string, any>) => {
    const deviceTypeAttributes = dt.attributes.attributes

    return {
      id: dt.id,
      brand_alias: deviceTypeAttributes.brand_alias,
      alias: deviceTypeAttributes.alias,
      type: deviceTypeAttributes.type,
      supported: deviceTypeAttributes.supported
    }
  })
}
